import { Inject, Injectable } from '@angular/core';
import { RouterModule, DefaultUrlSerializer, UrlTree, UrlSerializer, Router } from '@angular/router';

import { AccountDataStore } from "./accountData";
import { GlobalDataStore } from "./globalData";
import { ValueSubscriptionManager } from "./valueSubscriptionManager";
import { ManagedSubject } from "../../../shared/managedSubject";
import { ManagedSubscription, SubscriptionOptions } from "../../../shared/managedSubscription";
import { AppActionCreator } from "./appActionCreator";
import { User, LocalStorageData, SystemInformation } from "../models";
import { StartupArguments } from "./startupArguments";

@Injectable()
export class ApplicationLifeCycleService {

  public userUpdateSubject: ManagedSubject<User>;
  public reloadCacheSubject: ManagedSubject<void>;

  constructor(
    @Inject(GlobalDataStore) public globalDataStore: GlobalDataStore,
    @Inject(AccountDataStore) public accountStore: AccountDataStore,
    @Inject(ValueSubscriptionManager) public valueSubscriptionManager: ValueSubscriptionManager,
    @Inject(AppActionCreator) public appActionCreator: AppActionCreator,
    public startupArguments: StartupArguments
  )
  {
    this.userUpdateSubject = this.valueSubscriptionManager.createSubject<User>(null, false, 1, false);
    this.reloadCacheSubject = this.valueSubscriptionManager.createSubject<void>(null, false, 1, false);
  }

  beforeApplicationInitialize() {
    return this.fetchGlobalDataWithUser(this.startupArguments.locale);
  }

  onUserUpdated(subscriptionOptions: SubscriptionOptions<User>) {
    return this.userUpdateSubject.subscribe(subscriptionOptions);
  }

  /**
   * Call after updating user. The callback is executed after performing cleanup of client cache
   * @param callback
   */
  userUpdated(callback?: (user: User) => void) {

    // Purge all caches after user login, user changes, so that fresh product data is loaded based on the User
    this.appActionCreator.dispatchClearClientCache();
    this.fetchGlobalData().subscribe(result => {

      if (result) {

        let user = this.accountStore.getUser();

        if (callback) callback(user);
        this.userUpdateSubject.nextValue(user);
      }
    });
  }

  onCacheReloaded(subscriptionOptions: SubscriptionOptions<void>) {
    return this.reloadCacheSubject.subscribe(subscriptionOptions);
  }

  /**
   * Call after reloading of cache. Callback is called after cleanup of client cache
   * @param callback
   */
  cacheReloaded(callback?: Function) {

    this.appActionCreator.dispatchClearClientCache();
    this.fetchGlobalData().subscribe(result => {
      if (result) {
        if (callback) callback();
        this.reloadCacheSubject.nextValue(void(0));
      }
    })
  }

  protected fetchGlobalDataWithUser(locale?:string) {

    let managedSubject = this.valueSubscriptionManager.createSubject<boolean>(ManagedSubject.IGNORE_VALUE, true);

    // Get GlobalData and then fetch User
    this.fetchGlobalData(locale).subscribe(result => {

      if (result) {
        this.accountStore.getLoggedInUser().toPromise().then(() => managedSubject.nextValue(true), () => managedSubject.nextValue(false))
      }
      else {
        managedSubject.nextValue(false);
      }

    });

    return managedSubject;
  }

  // Get GlobalData such as Strings, GlobalSettings and WebSettings
  protected fetchGlobalData(locale?: string) {

    let allPromise = Promise.all([this.globalDataStore.getLocalizeData(locale, true).toPromise(),
    this.globalDataStore.getGlobalSettings(true).toPromise(),
    this.globalDataStore.getWebSettings(true).toPromise(),
    this.globalDataStore.getUISettings(true).toPromise(),
    this.globalDataStore.getEntities(SystemInformation.name, true).toPromise()]);

    let managedSubject = this.valueSubscriptionManager.createSubject<boolean>(ManagedSubject.IGNORE_VALUE, true);

    allPromise.then(() => managedSubject.nextValue(true), () => managedSubject.nextValue(false));

    return managedSubject;
  }
}